/**
 * @trovex/auth React wrapper — the app-facing hook around the
 * framework-agnostic core (modules/auth/core/authCore.js).
 *
 * Ported from apps/voip/src/hooks/useAuth.js, preserving its transient
 * null-user handling: once this browser has had a confirmed session,
 * momentary null-user states (token refresh blip, claim-propagation race)
 * surface the inline Login screen rather than bouncing to id.trovex.com.
 *
 * Usage:
 *   const core = useMemo(() => createAuthCore({ ... }), []);
 *   const { user, loading, accessDenied, ... } = useTrovexAuth(core);
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
// Relative, not '@trovex/analytics': modules/auth is already resolved by each
// app's alias, so a relative hop resolves on disk and no consumer has to add a
// second alias to pick this up. The plugin imports nothing itself, so it adds
// no dependency to any bundle that did not already have one.
import { createTelemetry } from '../../analytics/index.js';

export function useTrovexAuth(core, options = {}) {
  const {
    // Called once per confirmed sign-in with (firebaseUser, email) — apps
    // hang side effects here (voip's users/{uid} self-register, Sentry
    // user attribution) without the shared hook knowing about them.
    onSignedIn,
    timeoutMs = 10000,
    // Optimistic platform-access gating: render the signed-in app
    // immediately and run checkPlatformAccess in the background, revoking
    // only if it comes back false. Keeps the (fail-open, soft) gate off
    // the critical path to interactive — projects-portal's pre-migration
    // behaviour. Default false = users.trovex.com's awaited gate.
    optimisticAccess = false,
    // Skip the platformAccess/{id} soft gate entirely. For apps that have
    // NEVER gated on platformAccess (eshot/Broadcast: sign-in is open to
    // all staff, admin gating is its own concern) — migrating them must
    // not introduce a gate, even if someone later creates the
    // platformAccess doc. Default false.
    disableAccessGate = false,
    // Page-view telemetry, feeding systems.trovex.com/reporting. ON by design:
    // the owner asked for one plugin across every app (2026-08-30), and an
    // opt-in flag would mean the report silently missed whichever apps nobody
    // remembered to set it on. `telemetryAppId` overrides the id when an app's
    // platformId is not the one platformAccess uses; passing false disables it.
    telemetry = true,
    telemetryAppId,
  } = options;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  // Built once per app, and inert until a signed-in token exists. Every failure
  // inside it is a silent no-op, so it cannot affect sign-in.
  //
  // Constructed in an EFFECT, not during render: createTelemetry registers
  // document listeners and a flush interval, and a render-phase construction
  // leaks a set of both on every StrictMode double-render (and on any remount).
  // The effect also gives it a stop() on unmount, which flushes what is queued.
  const telemetryRef = useRef(null);
  useEffect(() => {
    if (!telemetry) return undefined;
    telemetryRef.current = createTelemetry({
      appId: telemetryAppId || core.platformId,
      getIdToken: () => (core.auth.currentUser ? core.auth.currentUser.getIdToken() : null),
    });
    return () => {
      const instance = telemetryRef.current;
      telemetryRef.current = null;
      if (instance) instance.stop();
    };
    // core is created once by the app (useMemo) and the ids are constants.
  }, [telemetry, telemetryAppId, core]);
  const authResolved = useRef(false);
  // Whether we've seen an authenticated user this session — gates the
  // transient-blip handling below.
  const everHadUser = useRef(false);
  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;

  useEffect(() => {
    if (!core) return undefined;
    let unsub = null;

    const timeoutId = setTimeout(() => {
      if (!authResolved.current) {
        console.warn(`[${core.storagePrefix}] Auth timeout after ${timeoutMs / 1000}s`);
        setLoading(false);
      }
    }, timeoutMs);

    (async () => {
      // 1. Persistence, then 2. SSO token exchange (if present) BEFORE
      // listening for auth state — ordering matters, see voip's useAuth.
      await core.applyPersistence();
      await core.consumeSsoToken();

      // 3. Auth state listener.
      const auth = core.auth;
      unsub = onAuthStateChanged(auth, async (firebaseUser) => {
        clearTimeout(timeoutId);
        authResolved.current = true;

        const { email, fromCache } = await core.resolveUserEmail(firebaseUser);
        const priorSession = core.hasPriorSession();

        if (firebaseUser && email && core.isAllowedEmail(email)) {
          if (disableAccessGate) {
            // No platform-access gate for this app — fall through to the
            // signed-in path below.
          } else if (!optimisticAccess) {
            const hasAccess = await core.checkPlatformAccess(email);
            if (!hasAccess) {
              setAccessDenied(true);
              setUser(null);
              setLoading(false);
              return;
            }
          } else {
            // Background check; revoke only on an explicit deny (the core
            // fails open on errors, matching the old inline behaviour).
            core.checkPlatformAccess(email)
              .then((hasAccess) => {
                if (!hasAccess) { setAccessDenied(true); setUser(null); }
              })
              .catch(() => { /* fail open — server callables are the boundary */ });
          }
          setAccessDenied(false);
          setUser(firebaseUser);
          everHadUser.current = true;
          core.markSessionEstablished(firebaseUser.uid, email, { fromCache });
          try { onSignedInRef.current?.(firebaseUser, email); } catch { /* app side effects must not break auth */ }
        } else if (firebaseUser && !email && (everHadUser.current || priorSession)) {
          // Firebase user with no resolvable Trovex email on a browser
          // that has had a confirmed session — transient (network blip or
          // claim-propagation race). Keep the session; a background token
          // refresh re-validates shortly.
          console.warn(`[${core.storagePrefix}] auth: user with no resolvable email on a known session — keeping it (transient)`);
          setAccessDenied(false);
          setUser(firebaseUser);
          everHadUser.current = true;
          setLoading(false);
          firebaseUser.getIdToken(true).catch(() => {});
          return;
        } else {
          if (firebaseUser) signOut(auth);
          setUser(null);
          // Loop breaker: if SSO was attempted and still no user, fall
          // through to the inline Login screen (never redirect-loop).
          core.consumeSsoAttemptedFlag();
        }
        setLoading(false);
      });
    })();

    return () => { clearTimeout(timeoutId); if (unsub) unsub(); };
  }, [core, timeoutMs]);

  const logout = useCallback(() => core.logout(), [core]);
  const loginWithTrovexId = useCallback(() => core.loginWithTrovexId(), [core]);
  const requestEmailCode = useCallback((email) => core.requestEmailCode(email), [core]);
  const verifyEmailCode = useCallback((email, code) => core.verifyEmailCode(email, code), [core]);

  // Exposed so an app can record its own navigations:
  //   const { trackPageView } = useAuth();
  //   useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  // An app that never calls it still reports its sign-ins, which the sign-in
  // log has always carried - it simply has no page-level depth.
  const trackPageView = useCallback((path) => {
    if (telemetryRef.current) telemetryRef.current.trackPageView(path);
  }, []);

  return {
    user, loading, accessDenied, logout, loginWithTrovexId,
    requestEmailCode, verifyEmailCode, trackPageView,
  };
}
